import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { KnowledgeTaskCard } from "./KnowledgeTaskCard";
import { useSearchKnowledgeItems, KnowledgeApi } from "@/lib/api/knowledge";
import { cn } from "@/lib/utils";
import useDebounce from "@/section/otc/useDebounce";

interface KnowledgeSearchComponentProps {
  className?: string;
}

export function KnowledgeSearchComponent({ className }: KnowledgeSearchComponentProps) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const debouncedQuery = useDebounce(query, 300);

  // Reset to first page when search query changes
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  const searchResults = useSearchKnowledgeItems({
    query: debouncedQuery,
    page,
    enabled: debouncedQuery.length > 0,
  });

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setQuery("");
    setPage(1);
  };

  const tasks = searchResults?.data?.data?.data || [];
  const pagination = searchResults?.data?.data?.pagination;
  const isLoading = searchResults.isLoading || searchResults.isFetching;
  const hasSearched = debouncedQuery.length > 0;

  const PaginationControls = () => {
    if (!pagination || !hasSearched) return null;

    return (
      <div className="flex items-center justify-between mt-4">
        <Button
          onClick={() => setPage(page - 1)}
          disabled={page <= 1 || isLoading}
          className={cn(isLoading ? "flex items-center justify-center" : "")}
        >
          {(query && isLoading) ? <span>Loading...</span> : <span>Previous</span>}
        </Button>

        <span className="text-sm text-muted-foreground">
          Page {pagination.currentPage || 1} of {pagination.totalPages || 1}
          {" "}({pagination.totalItems || 0} results)
        </span>

        <Button
          onClick={() => setPage(page + 1)}
          disabled={page >= (pagination.totalPages || 1) || isLoading}
          className={cn(isLoading ? "flex items-center justify-center" : "")}
        >
          {(query && isLoading) ? <span>Loading...</span> : <span>Next</span>}
        </Button>
      </div>
    );
  };

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search tasks by title..."
            value={query}
            onChange={handleQueryChange}
            className="pl-10"
          />
        </div>
        {query && (
          <Button variant="outline" onClick={handleClearSearch}>
            Clear
          </Button>
        )}
      </div>

      {(query && isLoading) && (
        <div className="flex items-center justify-center p-8">
          <span>Loading...</span>
        </div>
      )}

      {!isLoading && hasSearched && tasks.length === 0 && (
        <div className="border rounded-lg p-6 bg-background text-center">
          <h3 className="font-medium mb-2">No results found</h3>
          <p className="text-sm text-muted-foreground">
            No tasks match your search query
          </p>
        </div>
      )}

      {!isLoading && hasSearched && tasks.length > 0 && (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {tasks.map((task: KnowledgeApi.KnowledgeItem) => (
              <KnowledgeTaskCard
                key={`search-${task.id}`}
                task={task}
                isAssigned={false}
                assignmentCount={task.assignmentsCount || 0}
                isStarred={task.is_starred || false}
              />
            ))}
          </div>
          <PaginationControls />
        </>
      )}

      {!hasSearched && (
        <div className="border rounded-lg p-6 bg-background text-center">
          <Search className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
          <h3 className="font-medium mb-2">Search for Tasks</h3>
          <p className="text-sm text-muted-foreground">
            Enter a search query to find tasks by title
          </p>
        </div>
      )}
    </div>
  );
}
